import React, { useEffect } from 'react';
import styled from 'styled-components';
import Seo from '../components/common/Seo';
import { colors, breakpoints } from '../styles/variables';
import { coursesData } from '../data/coursesData';

const Page = styled.div`
  background: ${colors.lightGray};
`;

const Hero = styled.section`
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 140%);
  color: ${colors.white};
  padding: 120px 24px 56px;
`;

const HeroInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 38px;
  font-weight: 700;
  margin: 0 0 14px;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 28px;
  }
`;

const Lead = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.85);
  max-width: 760px;
  margin: 0;
`;

const Section = styled.section`
  max-width: 1100px;
  margin: 0 auto;
  padding: 80px 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
`;

const Card = styled.article`
  background: ${colors.white};
  border-radius: 12px;
  padding: 28px 26px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
`;

const Stars = styled.div`
  color: #ffd600;
  font-size: 18px;
  margin-bottom: 10px;
`;

const Quote = styled.p`
  font-size: 15px;
  line-height: 1.8;
  color: ${colors.textGray};
  margin: 0 0 20px;
  flex-grow: 1;
`;

const Meta = styled.div`
  border-top: 1px solid ${colors.lightGray};
  padding-top: 16px;
`;

const Name = styled.div`
  font-weight: 700;
  color: ${colors.primary};
  font-size: 14px;
`;

const Role = styled.div`
  font-size: 13px;
  color: ${colors.tertiary};
  margin-top: 2px;
`;

const CourseTag = styled.span`
  display: inline-block;
  margin-top: 10px;
  background: rgba(0, 196, 113, 0.12);
  color: ${colors.secondary};
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
`;

const Notice = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  padding: 20px 24px;
  margin-top: 32px;
  text-align: center;
  font-size: 14px;
  color: ${colors.textGray};
`;

const maskName = (name) => name.replace(/0/g, '〇');

const ReviewsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 모든 코스의 후기를 평탄화하면서 코스 제목을 함께 보존
  const allReviews = coursesData.flatMap((course) =>
    (course.reviews || []).map((review) => ({
      ...review,
      courseTitle: course.title,
    }))
  );

  return (
    <Page>
      <Seo
        title="수강생 후기"
        description="ElectroWave Club과 함께한 학생들의 솔직한 후기. 1:1 줌 멘토 매칭과 프로젝트 중심 학습이 어떻게 작동하는지 직접 확인하세요."
        path="/reviews"
        ogType="website"
      />

      <Hero>
        <HeroInner>
          <Title>먼저 다녀간 학생들의 이야기</Title>
          <Lead>
            클럽에 참여했던 학생들이 직접 남긴 후기입니다. 거창한 성공담보다는, 각자 어디서 막혀 있었고 어떤
            지점이 도움이 됐는지에 초점을 맞춰 받았습니다.
          </Lead>
        </HeroInner>
      </Hero>

      <Section>
        <Grid>
          {allReviews.map((r, idx) => (
            <Card key={idx}>
              <Stars>{'★'.repeat(r.stars || 5)}</Stars>
              <Quote>“{r.text}”</Quote>
              <Meta>
                <Name>- {maskName(r.name)}</Name>
                {r.role && <Role>{r.role}</Role>}
                <CourseTag>{r.courseTitle}</CourseTag>
              </Meta>
            </Card>
          ))}
        </Grid>
        <Notice>
          후기는 본인 동의를 받아 게재했으며, 이름은 가운데 글자만 공개합니다.
        </Notice>
      </Section>
    </Page>
  );
};

export default ReviewsPage;
